import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Download, Maximize2, Loader2, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { FileThumb } from "./FileThumb";
import { downloadFile, fileType, getBlob, openPdf } from "../lib/attachments";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function PdfPage({ doc, n, rot, width }) {
  const ref = useRef(null);
  useEffect(() => {
    let task;
    let alive = true;
    doc.getPage(n).then((page) => {
      if (!alive || !ref.current) return;
      const base = page.getViewport({ scale: 1, rotation: rot });
      const vp = page.getViewport({ scale: 1400 / base.width, rotation: rot });
      const c = ref.current;
      c.width = vp.width;
      c.height = vp.height;
      task = page.render({ canvasContext: c.getContext("2d"), viewport: vp });
      task.promise.catch(() => {});
    }).catch(() => {});
    return () => { alive = false; task?.cancel?.(); };
  }, [doc, n, rot]);
  return <canvas ref={ref} className="bg-white shadow-lg block mx-auto" style={{ width }} data-testid={`viewer-pdf-page-${n}`} />;
}

function PdfView({ dossierId, file, zoom, rot }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setDoc(null);
    setErr(false);
    openPdf(dossierId, file).then((d) => alive && setDoc(d)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [dossierId, file]);
  if (err) return <Unavailable file={file} dossierId={dossierId} msg="Impossible d'afficher ce PDF" />;
  if (!doc) return <Loader2 className="w-6 h-6 animate-spin text-white/70 m-auto" />;
  const width = `${Math.round(820 * zoom)}px`;
  return (
    <div className="w-full h-full overflow-auto py-6 space-y-4" data-testid="viewer-pdf">
      {Array.from({ length: doc.numPages }, (_, i) => <PdfPage key={i} doc={doc} n={i + 1} rot={rot} width={width} />)}
      <div className="text-center text-[11px] text-white/50">{doc.numPages} page{doc.numPages > 1 ? "s" : ""}</div>
    </div>
  );
}

function ImageView({ dossierId, file, zoom, rot, setZoom }) {
  const [src, setSrc] = useState(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  useEffect(() => {
    let alive = true;
    setSrc(null);
    setPan({ x: 0, y: 0 });
    getBlob(dossierId, file).then((b) => alive && setSrc(URL.createObjectURL(b))).catch(() => toast.error("Image introuvable"));
    return () => { alive = false; };
  }, [dossierId, file]);
  useEffect(() => { if (zoom <= 1) setPan({ x: 0, y: 0 }); }, [zoom]);
  if (!src) return <Loader2 className="w-6 h-6 animate-spin text-white/70 m-auto" />;
  return (
    <div
      className={`w-full h-full flex items-center justify-center overflow-hidden ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
      onWheel={(e) => setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.15 : 0.87), 0.5, 6))}
      onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2.5))}
      onPointerDown={(e) => { drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; e.currentTarget.setPointerCapture(e.pointerId); }}
      onPointerMove={(e) => drag.current && zoom > 1 && setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y })}
      onPointerUp={() => { drag.current = null; }}
      data-testid="viewer-image-stage"
    >
      <img src={src} alt={file.filename} draggable={false}
        className="max-w-[92%] max-h-[92%] object-contain select-none shadow-2xl transition-transform duration-150"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rot}deg)` }} data-testid="viewer-image" />
    </div>
  );
}

function Unavailable({ dossierId, file, msg = "Aperçu indisponible" }) {
  return (
    <div className="m-auto bg-white rounded-lg p-8 text-center max-w-sm" data-testid="viewer-unavailable">
      <FileWarning className="w-10 h-10 mx-auto text-norea-textMuted mb-3" />
      <div className="font-medium text-norea-text mb-1">{msg}</div>
      <div className="text-xs text-norea-textMuted mb-4 break-all">{file.filename}</div>
      <button onClick={() => downloadFile(dossierId, file)} className="btn-primary text-xs mx-auto" data-testid="viewer-unavailable-download"><Download className="w-3.5 h-3.5" /> Télécharger</button>
    </div>
  );
}

const ToolBtn = ({ onClick, title, children, testid }) => (
  <button onClick={onClick} title={title} className="p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-testid={testid}>{children}</button>
);

export default function AttachmentViewer({ dossierId, items, startKey, onClose }) {
  const [tab, setTab] = useState("");
  const list = useMemo(() => items.filter((f) => !tab || f.tab === tab), [items, tab]);
  const [i, setI] = useState(() => Math.max(0, items.findIndex((f) => f.key === startKey)));
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const stripRef = useRef(null);
  const idx = clamp(i, 0, Math.max(0, list.length - 1));
  const file = list[idx];
  const tabs = useMemo(() => {
    const m = new Map();
    items.forEach((f) => m.set(f.tab, { label: f.tab_label, n: (m.get(f.tab)?.n || 0) + 1 }));
    return [...m.entries()];
  }, [items]);

  const go = useCallback((d) => { setI((x) => (list.length ? (clamp(x, 0, list.length - 1) + d + list.length) % list.length : 0)); setZoom(1); setRot(0); }, [list.length]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") setZoom((z) => clamp(z * 1.25, 0.5, 6));
      else if (e.key === "-") setZoom((z) => clamp(z / 1.25, 0.5, 6));
      else if (e.key.toLowerCase() === "r") setRot((r) => (r + 90) % 360);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [go, onClose]);
  useEffect(() => { stripRef.current?.querySelector(`[data-active="1"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }); }, [idx, tab]);

  const type = file ? fileType(file) : null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0e1411]/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-150" data-testid="attachment-viewer">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-medium truncate" data-testid="viewer-filename">{file?.filename || "—"}</div>
          <div className="text-[11px] text-white/50 truncate">{file ? `${file.tab_label}${file.label ? ` · ${file.label}` : ""}` : ""}</div>
        </div>
        <span className="text-xs text-white/60 tabular-nums" data-testid="viewer-counter">{list.length ? idx + 1 : 0} / {list.length}</span>
        <div className="flex items-center">
          <ToolBtn onClick={() => setZoom((z) => clamp(z / 1.25, 0.5, 6))} title="Dézoomer (−)" testid="viewer-zoom-out"><ZoomOut className="w-4 h-4" /></ToolBtn>
          <span className="text-[11px] text-white/60 w-10 text-center tabular-nums" data-testid="viewer-zoom-level">{Math.round(zoom * 100)}%</span>
          <ToolBtn onClick={() => setZoom((z) => clamp(z * 1.25, 0.5, 6))} title="Zoomer (+)" testid="viewer-zoom-in"><ZoomIn className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={() => { setZoom(1); setRot(0); }} title="Taille d'origine" testid="viewer-reset"><Maximize2 className="w-4 h-4" /></ToolBtn>
          <ToolBtn onClick={() => setRot((r) => (r + 90) % 360)} title="Pivoter (R)" testid="viewer-rotate"><RotateCw className="w-4 h-4" /></ToolBtn>
          {file && <ToolBtn onClick={() => downloadFile(dossierId, file)} title="Télécharger" testid="viewer-download"><Download className="w-4 h-4" /></ToolBtn>}
          <ToolBtn onClick={onClose} title="Fermer (Échap)" testid="viewer-close"><X className="w-5 h-5" /></ToolBtn>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="flex items-center gap-1.5 px-4 py-2 overflow-x-auto" data-testid="viewer-tab-filters">
          {[["", { label: "Tout", n: items.length }], ...tabs].map(([k, t]) => (
            <button key={k || "all"} onClick={() => { setTab(k); setI(0); setZoom(1); setRot(0); }}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${tab === k ? "bg-white text-norea-text" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
              data-testid={`viewer-tab-${k || "all"}`}>{t.label} <span className="opacity-60">{t.n}</span></button>
          ))}
        </div>
      )}

      <div className="relative flex-1 min-h-0 flex">
        {list.length > 1 && (
          <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-black/40 text-white hover:bg-black/70 transition-colors" data-testid="viewer-prev"><ChevronLeft className="w-6 h-6" /></button>
        )}
        <div className="flex-1 min-w-0 flex" key={file?.key}>
          {!file ? <div className="m-auto text-white/60 text-sm">Aucune pièce jointe</div>
            : type === "image" ? <ImageView dossierId={dossierId} file={file} zoom={zoom} rot={rot} setZoom={setZoom} />
            : type === "pdf" ? <PdfView dossierId={dossierId} file={file} zoom={zoom} rot={rot} />
            : <Unavailable dossierId={dossierId} file={file} />}
        </div>
        {list.length > 1 && (
          <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-black/40 text-white hover:bg-black/70 transition-colors" data-testid="viewer-next"><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>

      <div ref={stripRef} className="flex gap-2 px-4 py-3 border-t border-white/10 overflow-x-auto" data-testid="viewer-strip">
        {list.map((f, j) => (
          <button key={f.key} onClick={() => { setI(j); setZoom(1); setRot(0); }} data-active={j === idx ? "1" : "0"} title={f.filename}
            className={`flex-shrink-0 w-14 h-[72px] rounded overflow-hidden transition-[opacity,transform] ${j === idx ? "ring-2 ring-white scale-105" : "opacity-50 hover:opacity-100"}`}
            data-testid={`viewer-thumb-${j}`}>
            <FileThumb dossierId={dossierId} file={f} />
          </button>
        ))}
      </div>
    </div>
  );
}
